//-- Copyright (c) 2021 TMT Insights Group, LLC
import { convertStylesToStyled } from '@/_utils/utils';
import PolarisIcon from '@/components/_common/PolarisIcon';
import styled from '@emotion/styled';
import { Badge, BadgeProps } from '@mui/material';

type TabBadgeProps = BadgeProps & {
  label?: string;
  count: number;
  loading?: boolean;
  color?: BadgeProps['color'] | 'pending';
};

const TabBadge = ({ label, count, loading = false, color = 'primary' }: TabBadgeProps) => {
  const showBadge = () => {
    if (loading) {
      return (
        <Spinner>
          <LoadingIcon icon="loading_dots" />
        </Spinner>
      );
    } else if (typeof count === 'number') {
      return (
        <CountBadge sx={{ backgroundColor: `${color}.main` }} max={99}>
          {count?.toLocaleString()}
        </CountBadge>
      );
    }
  };

  return (
    <TabBadgeWrapper>
      <div>{label}</div>
      {showBadge()}
    </TabBadgeWrapper>
  );
};

export default TabBadge;

const Spinner = styled.span`
  display: inline-flex;
  position: relative;
  margin-left: ${({ theme }) => theme.spacing(1.5)};
  color: ${({ theme }) => theme.palette.primary.main};
`;

const LoadingIcon = styled(PolarisIcon)`
  height: 2rem;
  width: 2rem;
`;

const CountBadge = styled(Badge)`
  display: inline-flex;
  border-radius: 10px;
  padding: 2px;
  height: 1.43rem;
  font-size: 0.9rem;
  min-width: 1.43rem;
  line-height: 1.43rem;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-left: ${({ theme }) => theme.spacing(1)};
  color: ${({ theme }) => theme.palette.getContrastText(theme.palette.text.primary)};
`;

const TabBadgeWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  ${({ theme }) => convertStylesToStyled(theme.customOverrides?.TabBadge)}
`;
